import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { packageAPI } from '../services/api';

import { useI18n } from '../i18n/i18nContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNoIndex } from '../hooks/useNoIndex';
import { useBookingCartStore } from '../stores/bookingCartStore';
import { SuggestedServices } from '../components/SuggestedServices';
import { BookingCart } from '../components/BookingCart';
import { Toast } from '../components/Toast';
import { Package, Service } from '@shared/types';
import { pushToDataLayer, clearEcommerce, formatPackageItem } from '../utils/dataLayer';
import ServiceBooking from 'src/components/ServiceBooking';

const BookingPackageCalendarPage: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useLocalizedNavigate();
  const { t } = useI18n();
  const { items } = useBookingCartStore();

  useDocumentTitle(t.booking.selectDateTime);
  useNoIndex();

  const [pkg, setPkg] = useState<Package | null>(null);

  const [loading, setLoading] = useState(true);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [lastOperationWasChange, setLastOperationWasChange] = useState(false);
  const [allPackageServicesBooked, setAllPackageServicesBooked] = useState(false);

  const hasTrackedViewItem = useRef(false);

  useEffect(() => {
    if (packageId) {
      fetchPackage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId]);

  useEffect(() => {
    if (!pkg) {
      setAllPackageServicesBooked(false);
      return;
    }

    const packageCartItem = items.find((item) => item.type === 'package' && item.id === pkg.id);
    setAllPackageServicesBooked(
      (packageCartItem?.services?.length || 0) === (pkg.services?.length || 0)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pkg]);

  useEffect(() => {
    if (pkg && !hasTrackedViewItem.current) {
      hasTrackedViewItem.current = true;
      clearEcommerce();
      pushToDataLayer('view_item', {
        content: { category: 'booking_package' },
        ecommerce: {
          items: [formatPackageItem(pkg)],
        },
      });
    }
  }, [pkg]);

  const fetchPackage = async () => {
    if (!packageId) return;

    try {
      const response = await packageAPI.getById(packageId);
      setPkg(response.data);
    } catch (err) {
      console.error(t.console.errors.fetchingService, err);
      navigate('/book');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  if (!pkg) {
    return null;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {showCartNotification && (
        <Toast
          message={
            lastOperationWasChange
              ? t.cart.bookingChanged || 'Booking has been changed'
              : t.cart.addedToCart
          }
          type="success"
          onClose={() => setShowCartNotification(false)}
        />
      )}

      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">{t.booking.title}</h1>
        <p className="text-lg text-gray-600">{t.booking.step2ChooseDateTime}</p>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <div>
          {pkg?.services?.map((service: Service) => (
            <ServiceBooking
              key={service.id}
              pkg={pkg}
              service={service}
              setLastOperationWasChange={setLastOperationWasChange}
              setShowCartNotification={setShowCartNotification}
            />
          ))}

          {/* Action Buttons - Below date/time selection, spanning 4 columns */}
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:col-span-4 lg:col-start-4">
            <button
              onClick={() => navigate('/book')}
              className="w-full rounded-md border border-gray-300 px-6 py-2 font-medium text-gray-700 hover:bg-gray-50 sm:w-auto"
            >
              {t.cart.addAnotherService}
            </button>
          </div>

          {/* Booking Cart - Below action buttons, spanning 4 columns */}
          {items.length > 0 && (
            <div className="lg:col-span-4 lg:col-start-4">
              <BookingCart hideCheckoutButton={!allPackageServicesBooked} />
            </div>
          )}

          <SuggestedServices />
        </div>
      </div>
    </div>
  );
};

export default BookingPackageCalendarPage;
